import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Area, AreaChart, ScatterChart, Scatter, ZAxis, ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, ArrowUp, ArrowDown, AlertOctagon } from "lucide-react";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import SizedChart from "../components/SizedChart";
import ExportButton from "../components/ExportButton";
import { downloadCSV, stamp } from "../lib/csv";
import { fmtInr, fmtInt, fmtPct, monthLabel } from "../lib/format";

const Analytics = () => {
  const { session, focusBranchId } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/analytics", { params: scopeParams(session, focusBranchId) })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, focusBranchId]);

  // Nudge recharts ResponsiveContainer to remeasure after data arrives —
  // avoids the "SVG has 0 width" first-paint issue when data loads async.
  useEffect(() => {
    if (!data) return;
    const t1 = setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
    const t2 = setTimeout(() => window.dispatchEvent(new Event("resize")), 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [data]);

  if (loading || !data) {
    return (
      <div className="ang-panel p-10 text-center text-sm text-ang-muted" data-testid="analytics-loading">
        Building analytics…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <ExecutiveStrip exec={data.executive} />
      <ForecastSection forecast={data.revenue_forecast} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RankMovementCard movement={data.rank_movement} />
        <AnomaliesCard anomalies={data.anomalies} />
      </div>
      <RevenueHeatmap heatmap={data.revenue_heatmap} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <CategoryMixDrift mix={data.category_mix} />
        <PrivateLabelTrend trend={data.private_label_trend} />
      </div>
      <BranchEfficiency efficiency={data.branch_efficiency} />
      <HourlyDowHeatmap hourly={data.hourly_dow} />
      <BasketAffinity affinity={data.basket_affinity} />
      <PlanogramSuggestions affinity={data.basket_affinity} />
      <CategoryGrowthTable cats={data.cat_growth} />
    </div>
  );
};

// =========================================================================

const BasketAffinity = ({ affinity }) => {
  if (!affinity) return null;
  const { category_matrix, category_labels, top_pairs, insights } = affinity;

  const cellStyle = (lift, self) => {
    if (self) return { background: "#E7E4DC", color: "#6B6B7B" };
    // 0.4 (cool) → 1.0 (neutral) → 2.7 (warm)
    if (lift >= 2.0) return { background: "rgba(201,161,92,0.85)", color: "#14142B" };
    if (lift >= 1.5) return { background: "rgba(201,161,92,0.55)", color: "#14142B" };
    if (lift >= 1.2) return { background: "rgba(201,161,92,0.28)", color: "#1C1C2E" };
    if (lift >= 1.0) return { background: "#FAF9F6", color: "#6B6B7B" };
    if (lift >= 0.8) return { background: "rgba(107,107,123,0.10)", color: "#6B6B7B" };
    return { background: "rgba(183,64,42,0.12)", color: "#B7402A" };
  };

  return (
    <>
      <SectionHeader
        eyebrow="Basket Affinity"
        title="Cross-sell intelligence · what customers buy together"
        rightSlot={
          <div className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-widest text-ang-muted">
            <span>lift &lt; 1 · anti-affinity</span>
            <span>lift ≥ 1.5 · strong</span>
            <span>lift ≥ 2 · exceptional</span>
          </div>
        }
      />

      {/* Insights row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          testId="ba-kpi-top-pair"
          label="Highest-lift pair"
          value={insights.highest_lift_pair ? insights.highest_lift_pair.lift.toFixed(2) : "—"}
          hint={insights.highest_lift_pair ? `${insights.highest_lift_pair.product_a.split(" · ")[0]} + ${insights.highest_lift_pair.product_b.split(" · ")[0]}` : ""}
        />
        <KpiCard
          testId="ba-kpi-private-share"
          label="Private label in top pairs"
          value={`${insights.private_pairs_share}%`}
          hint="Own-label SKUs appear on at least one side"
          tone={insights.private_pairs_share < 40 ? "warn" : "default"}
        />
        <KpiCard
          testId="ba-kpi-cross-cat"
          label="Cross-category pairs"
          value={`${insights.cross_category_pairs}/${top_pairs.length}`}
          hint="Signal for aisle-adjacency moves"
        />
      </div>

      {/* Category affinity matrix */}
      <section className="ang-panel">
        <header className="border-b border-ang px-6 py-4">
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Category × Category</div>
          <h3 className="font-serif text-xl mt-1">Lift matrix</h3>
        </header>
        <div className="overflow-x-auto p-4">
          <table className="text-xs mx-auto" data-testid="affinity-matrix">
            <thead>
              <tr>
                <th className="w-32 text-left px-2 py-1"></th>
                {category_labels.map((c, i) => (
                  <th key={i} className="min-w-[70px] text-center px-1 py-2 text-[10px] font-normal text-ang-muted"
                      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {category_matrix.map((row) => (
                <tr key={row.category_id}>
                  <td className="px-2 py-1 font-serif text-sm text-right pr-3 whitespace-nowrap">{row.category}</td>
                  {row.cells.map((c, j) => (
                    <td key={j}
                        className="text-center tabular-nums text-[11px] px-1 py-2 border border-white"
                        style={cellStyle(c.lift, c.self)}
                        title={`${row.category} × ${c.category}  ·  lift ${c.lift}  ·  joint ${c.joint_pct}%`}>
                      {c.self ? "·" : c.lift.toFixed(2)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-ang text-[10px] uppercase tracking-widest text-ang-muted">
          hover a cell for detail · diagonal = self-purchase
        </div>
      </section>

      {/* Top product pairs table */}
      <section className="ang-panel">
        <header className="border-b border-ang px-6 py-4 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Top Product Pairs</div>
            <h3 className="font-serif text-xl mt-1">Ranked by lift · 30-day baskets</h3>
          </div>
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-affinity"
            onClick={() => downloadCSV(
              `angadya-basket-affinity-${stamp()}.csv`,
              [
                { key: "product_a", label: "Product A" },
                { key: "category_a", label: "Category A" },
                { key: "product_b", label: "Product B" },
                { key: "category_b", label: "Category B" },
                { key: "support_pct", label: "Support %" },
                { key: "confidence_pct", label: "Confidence %" },
                { key: "lift", label: "Lift" },
              ],
              top_pairs,
            )}
          />
        </header>
        <table className="w-full text-sm" data-testid="top-pairs-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
              <th className="text-left px-4 py-3">Product A</th>
              <th className="text-left px-4 py-3">Product B</th>
              <th className="text-right px-4 py-3 w-24">Support</th>
              <th className="text-right px-4 py-3 w-28">Confidence</th>
              <th className="text-right px-4 py-3 w-24">Lift</th>
            </tr>
          </thead>
          <tbody>
            {top_pairs.map((p, i) => (
              <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium flex items-center gap-1.5">
                    {p.is_private_a && <span className="w-1 h-3.5 inline-block" style={{ background: "#C9A15C" }} />}
                    {p.product_a}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ang-muted">{p.category_a}</div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="font-medium flex items-center gap-1.5">
                    {p.is_private_b && <span className="w-1 h-3.5 inline-block" style={{ background: "#C9A15C" }} />}
                    {p.product_b}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ang-muted">{p.category_b}</div>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{p.support_pct}%</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{p.confidence_pct}%</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  <span
                    className="inline-block px-2 py-0.5 font-semibold"
                    style={cellStyle(p.lift, false)}
                  >
                    {p.lift}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
};

// =========================================================================

const ExecutiveStrip = ({ exec }) => (
  <>
    <SectionHeader eyebrow="Chain Executive" title="30-day vitals · with year-over-year context" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <KpiCard testId="an-kpi-rev30" label="Revenue · 30D"
        value={fmtInr(exec.revenue_30d, { compact: true })}
        delta={exec.revenue_30d_yoy_pct}
        hint="YoY" />
      <KpiCard testId="an-kpi-rev365" label="Revenue · 12M"
        value={fmtInr(exec.revenue_365d, { compact: true })}
        delta={exec.revenue_365d_yoy_pct}
        hint="YoY" />
      <KpiCard testId="an-kpi-priv" label="Own-Label Share"
        value={`${(exec.private_label_share * 100).toFixed(1)}%`}
        delta={exec.private_label_delta_pp}
        hint={`${exec.private_label_delta_pp >= 0 ? "+" : ""}${exec.private_label_delta_pp} pp vs. last yr`} />
      <KpiCard testId="an-kpi-basket" label="Avg. Basket · Today"
        value={fmtInr(exec.avg_basket)} hint={`${fmtInt(exec.footfall_today)} footfall`} />
      <KpiCard testId="an-kpi-movers" label="Rank Movers · 30D"
        value={fmtInt(exec.rank_movers)}
        hint="Branches moving ≥3 places" tone="warn" />
      <KpiCard testId="an-kpi-topgrowth" label="Top Growth · YoY"
        value={exec.top_growth_category?.category?.split(" ")[0] || "—"}
        delta={exec.top_growth_category?.yoy_pct}
        hint={exec.top_growth_category?.category} />
    </div>
  </>
);

// =========================================================================

const ForecastSection = ({ forecast }) => {
  const chartData = forecast.map((p) => ({
    week: p.week,
    actual: p.actual,
    forecast: p.forecast,
    lower: p.lower,
    upper: p.upper,
  }));
  return (
    <>
      <SectionHeader
        eyebrow="Revenue Trajectory"
        title="Last 12 weeks · 4-week forecast (80% band)"
        rightSlot={
          <div className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-widest text-ang-muted">
            <LegendDot color="#14142B" label="Actual" />
            <LegendDot color="#C9A15C" label="Forecast" />
          </div>
        }
      />
      <section className="ang-panel">
        <SizedChart height={340}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="week" axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v)}
                axisLine={false} tickLine={false} width={70}
              />
              <Tooltip
                formatter={(v, name) => v == null ? ["—", name] : [fmtInr(v), name]}
              />
              <Line type="monotone" dataKey="upper" stroke="#C9A15C" strokeWidth={1} strokeDasharray="2 3" strokeOpacity={0.5} dot={false} name="Upper 80%" connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="lower" stroke="#C9A15C" strokeWidth={1} strokeDasharray="2 3" strokeOpacity={0.5} dot={false} name="Lower 80%" connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="actual" stroke="#14142B" strokeWidth={2} dot={{ r: 3, fill: "#14142B" }} name="Actual" connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="forecast" stroke="#C9A15C" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3, fill: "#C9A15C" }} name="Forecast" connectNulls={false} isAnimationActive={false} />
            </LineChart>
          </SizedChart>
      </section>
    </>
  );
};

const LegendDot = ({ color, label, solid }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="inline-block w-3 h-1.5" style={{ background: color, opacity: solid ? 1 : 1 }} />
    {label}
  </span>
);

// =========================================================================

const RankMovementCard = ({ movement }) => (
  <section className="ang-panel">
    <header className="border-b border-ang px-6 py-4 flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ang-muted">Rank Movement</div>
        <h3 className="font-serif text-xl mt-1">30-day climbers &amp; fallers</h3>
      </div>
      <span className="text-[10px] uppercase tracking-widest text-ang-muted hidden md:inline">by 7D revenue rank</span>
    </header>
    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-ang">
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-ang-muted mb-3 flex items-center gap-1.5">
          <TrendingUp size={12} strokeWidth={2} style={{ color: "#2F8B5E" }} /> Climbers
        </div>
        <ul className="space-y-2" data-testid="rank-climbers">
          {movement.climbers.length === 0 && <li className="text-sm text-ang-muted">No significant climbers.</li>}
          {movement.climbers.map((m) => (
            <li key={m.branch_id} className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-serif text-sm truncate">{m.branch_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">{m.region} Chennai</div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">Rank</div>
                <div className="tabular-nums text-sm">
                  <span className="line-through text-ang-muted mr-1">{m.rank_prev}</span>
                  <span className="font-serif">{m.rank_now}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#2F8B5E" }}>▲ {m.delta}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-ang-muted mb-3 flex items-center gap-1.5">
          <TrendingDown size={12} strokeWidth={2} style={{ color: "#B7402A" }} /> Fallers
        </div>
        <ul className="space-y-2" data-testid="rank-fallers">
          {movement.fallers.length === 0 && <li className="text-sm text-ang-muted">No significant fallers.</li>}
          {movement.fallers.map((m) => (
            <li key={m.branch_id} className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-serif text-sm truncate">{m.branch_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">{m.region} Chennai</div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">Rank</div>
                <div className="tabular-nums text-sm">
                  <span className="line-through text-ang-muted mr-1">{m.rank_prev}</span>
                  <span className="font-serif">{m.rank_now}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#B7402A" }}>▼ {Math.abs(m.delta)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// =========================================================================

const AnomaliesCard = ({ anomalies }) => (
  <section className="ang-panel">
    <header className="border-b border-ang px-6 py-4 flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ang-muted">Anomaly Detection</div>
        <h3 className="font-serif text-xl mt-1">Branches deviating ≥1.5σ this week</h3>
      </div>
      <AlertOctagon size={14} strokeWidth={2} style={{ color: "#B8842A" }} />
    </header>
    <ul className="divide-y divide-ang" data-testid="anomalies-list">
      {anomalies.length === 0 && (
        <li className="px-6 py-8 text-sm text-ang-muted text-center">Nothing unusual — every branch is within its 8-week band.</li>
      )}
      {anomalies.map((a) => (
        <li key={a.branch_id} className="px-6 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-serif text-sm">{a.branch_name}</div>
            <div className="text-[11px] uppercase tracking-widest text-ang-muted">{a.region} Chennai</div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">This week</div>
              <div className="tabular-nums text-sm">{fmtInr(a.current_week, { compact: true })}</div>
            </div>
            <div className="text-right hidden md:block">
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">8W avg</div>
              <div className="tabular-nums text-sm text-ang-muted">{fmtInr(a.trailing_avg, { compact: true })}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">Deviation</div>
              <div className="tabular-nums text-sm font-semibold" style={{ color: a.direction === "up" ? "#2F8B5E" : "#B7402A" }}>
                {a.direction === "up" ? <ArrowUp size={12} className="inline mr-0.5" /> : <ArrowDown size={12} className="inline mr-0.5" />}
                {fmtPct(a.deviation_pct)}
                <span className="text-[10px] text-ang-muted ml-1">σ={a.z_score}</span>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  </section>
);

// =========================================================================

const RevenueHeatmap = ({ heatmap }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const rowMax = 8;  // days per column baseline for coloring cap
  const cellColor = (z) => {
    // z in [-3, 3]; map to red↔white↔green
    const t = Math.max(-2.5, Math.min(2.5, z)) / 2.5; // -1..1
    if (t >= 0) {
      // white to green
      const alpha = t * 0.85;
      return `rgba(47, 139, 94, ${alpha.toFixed(3)})`;
    }
    const alpha = -t * 0.85;
    return `rgba(183, 64, 42, ${alpha.toFixed(3)})`;
  };

  const exportHeatmapCSV = () => {
    const rows = [];
    heatmap.rows.forEach((r) => {
      const rec = { branch: r.branch_name, region: r.region, health: r.health_score };
      heatmap.week_labels.forEach((w, i) => { rec[w] = r.values[i]; });
      rows.push(rec);
    });
    const headers = [
      { key: "branch", label: "Branch" },
      { key: "region", label: "Region" },
      { key: "health", label: "Health" },
      ...heatmap.week_labels.map((w) => ({ key: w, label: w })),
    ];
    downloadCSV(`angadya-heatmap-${stamp()}.csv`, headers, rows);
  };

  return (
    <>
      <SectionHeader
        eyebrow="Revenue Heatmap"
        title="Branches × last 12 weeks · z-score by row"
        rightSlot={
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-heatmap"
            onClick={exportHeatmapCSV}
          />
        }
      />
      <section className="ang-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-heatmap">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b border-ang">
                <th className="text-left px-4 py-3 sticky left-0 bg-white z-10">Branch</th>
                <th className="text-center px-2 py-3 w-16">Health</th>
                {heatmap.week_labels.map((w) => (
                  <th key={w} className="text-center px-1 py-3 w-14 tabular-nums">{w}</th>
                ))}
                <th className="text-right px-3 py-3 w-20">Δ Last</th>
              </tr>
            </thead>
            <tbody>
              {heatmap.rows.map((r, i) => (
                <tr
                  key={r.branch_id}
                  onMouseEnter={() => setSelectedRow(r.branch_id)}
                  onMouseLeave={() => setSelectedRow(null)}
                  className={`border-b border-ang ${selectedRow === r.branch_id ? "bg-[#FAF9F6]" : ""}`}
                >
                  <td className="px-4 py-2 sticky left-0 bg-white">
                    <div className="font-serif text-sm">{r.branch_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-ang-muted">{r.region} Chennai</div>
                  </td>
                  <td className="text-center tabular-nums text-xs">{r.health_score}</td>
                  {r.values.map((v, j) => (
                    <td
                      key={j}
                      className="text-center tabular-nums text-[11px]"
                      style={{ background: cellColor(r.z_scores[j]) }}
                      title={`${heatmap.week_labels[j]}: ${fmtInr(v, { compact: true })}`}
                    >
                      {(v / 1e5).toFixed(1)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold"
                      style={{ color: r.last_delta_pct >= 0 ? "#2F8B5E" : "#B7402A" }}>
                    {fmtPct(r.last_delta_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-ang flex items-center justify-between text-[10px] uppercase tracking-widest text-ang-muted">
          <span>values in ₹ lakhs · color by within-row z-score</span>
          <div className="flex items-center gap-2">
            <span style={{ background: "rgba(183,64,42,0.7)" }} className="inline-block w-4 h-3" />
            <span>colder</span>
            <span className="inline-block w-4 h-3 border border-ang" />
            <span>avg</span>
            <span style={{ background: "rgba(47,139,94,0.7)" }} className="inline-block w-4 h-3" />
            <span>hotter</span>
          </div>
        </div>
      </section>
    </>
  );
};

// =========================================================================

const CATS = [
  { id: "groceries", label: "Groceries", color: "#14142B" },
  { id: "veg", label: "Vegetables", color: "#2F8B5E" },
  { id: "dairy", label: "Dairy", color: "#C9A15C" },
  { id: "gift", label: "Gift", color: "#B7402A" },
  { id: "toiletries", label: "Toiletries", color: "#B8842A" },
  { id: "bakery", label: "Bakery", color: "#6B6B7B" },
  { id: "household", label: "Household", color: "#a68140" },
  { id: "stationery", label: "Stationery", color: "#8b6b3f" },
  { id: "toys", label: "Toys", color: "#4d5480" },
];

const CategoryMixDrift = ({ mix }) => (
  <section className="ang-panel">
    <header className="border-b border-ang px-6 py-4">
      <div className="text-[10px] uppercase tracking-widest text-ang-muted">Category Mix Drift</div>
      <h3 className="font-serif text-xl mt-1">Revenue share by category · 24 months</h3>
    </header>
    <SizedChart height={300}>
        <AreaChart data={mix} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
          <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
          <XAxis dataKey="month" tickFormatter={monthLabel} minTickGap={40} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} axisLine={false} tickLine={false} width={45} domain={[0, 100]} />
          <Tooltip
            formatter={(v, name) => [`${Number(v).toFixed(1)}%`, name]}
            labelFormatter={monthLabel}
          />
          {CATS.map((c) => (
            <Area
              key={c.id}
              type="monotone"
              dataKey={c.id}
              stackId="1"
              stroke="none"
              fill={c.color}
              fillOpacity={0.85}
              name={c.label}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </SizedChart>
    <div className="px-6 pb-4 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-widest">
      {CATS.map((c) => (
        <span key={c.id} className="inline-flex items-center gap-1.5 text-ang-muted">
          <span className="inline-block w-3 h-2" style={{ background: c.color }} />
          {c.label}
        </span>
      ))}
    </div>
  </section>
);

// =========================================================================

const PrivateLabelTrend = ({ trend }) => (
  <section className="ang-panel">
    <header className="border-b border-ang px-6 py-4">
      <div className="text-[10px] uppercase tracking-widest text-ang-muted">Private-Label Penetration</div>
      <h3 className="font-serif text-xl mt-1">Own-label brand share · 24 months</h3>
    </header>
    <SizedChart height={300}>
        <LineChart data={trend} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
          <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
          <XAxis dataKey="month" tickFormatter={monthLabel} minTickGap={40} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={45} domain={[0, "auto"]} />
          <Tooltip formatter={(v) => [`${v}%`, "Share"]} labelFormatter={monthLabel} />
          <ReferenceLine y={30} stroke="#C9A15C" strokeDasharray="4 4" />
          <Line type="monotone" dataKey="share" stroke="#14142B" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#C9A15C" }} isAnimationActive={false} />
        </LineChart>
      </SizedChart>
    <div className="px-6 pb-3 text-[10px] uppercase tracking-widest text-ang-muted">Dashed tan line = 30% strategic target</div>
  </section>
);

// =========================================================================

const EfficiencyTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-ang p-3 text-xs">
      <div className="font-serif text-sm">{p.branch_name}</div>
      <div className="text-ang-muted text-[10px] uppercase tracking-widest">{p.region} Chennai · {p.size_sqft} sqft</div>
      <div className="mt-1.5 space-y-0.5">
        <div>Rev/sqft: <span className="tabular-nums">₹{p.revenue_per_sqft.toLocaleString("en-IN")}</span></div>
        <div>Health: <span className="tabular-nums">{p.health_score}</span></div>
        <div>30D Rev: <span className="tabular-nums">{fmtInr(p.revenue_30d, { compact: true })}</span></div>
      </div>
    </div>
  );
};

const HEALTH_BANDS = [
  { id: "green", label: "Green · ≥78", color: "#2F8B5E", test: (s) => s >= 78 },
  { id: "amber", label: "Amber · 62–77", color: "#B8842A", test: (s) => s >= 62 && s < 78 },
  { id: "red", label: "Red · <62", color: "#B7402A", test: (s) => s < 62 },
];

const BranchEfficiency = ({ efficiency }) => {
  const data = efficiency.map((e) => ({
    ...e,
    rev_per_sqft_display: e.revenue_per_sqft,
  }));
  const bandGroups = HEALTH_BANDS.map((b) => ({
    ...b,
    data: data.filter((d) => b.test(d.health_score)),
  }));
  return (
    <>
      <SectionHeader
        eyebrow="Branch Efficiency"
        title="Revenue-per-sq-ft × Health · size = footprint"
        rightSlot={
          <div className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-widest text-ang-muted">
            {HEALTH_BANDS.map((b) => (
              <span key={b.id} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: b.color }} />
                {b.label}
              </span>
            ))}
          </div>
        }
      />
      <section className="ang-panel">
        <SizedChart height={380}>
            <ScatterChart margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" />
              <XAxis
                type="number"
                dataKey="revenue_per_sqft"
                name="Revenue / sqft"
                tickFormatter={(v) => `₹${(v / 1000).toFixed(1)}K`}
                axisLine={false} tickLine={false}
              />
              <YAxis
                type="number" dataKey="health_score" name="Health"
                domain={[40, 100]}
                axisLine={false} tickLine={false}
              />
              <ZAxis type="number" dataKey="size_sqft" range={[60, 400]} name="Size" />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<EfficiencyTooltip />} />
              {bandGroups.map((g) => (
                <Scatter
                  key={g.id}
                  name={g.label}
                  data={g.data}
                  fill={g.color}
                  fillOpacity={0.78}
                  stroke={g.color}
                  strokeOpacity={0.9}
                  isAnimationActive={false}
                />
              ))}
            </ScatterChart>
          </SizedChart>
        <div className="px-6 pb-3 text-[10px] uppercase tracking-widest text-ang-muted flex items-center justify-between gap-3 flex-wrap">
          <span>x · revenue per sq-ft (30D) &nbsp;·&nbsp; y · health score &nbsp;·&nbsp; bubble · sq-ft</span>
          <span className="md:hidden flex items-center gap-2 flex-wrap">
            {HEALTH_BANDS.map((b) => (
              <span key={b.id} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: b.color }} />
                {b.label}
              </span>
            ))}
          </span>
        </div>
      </section>
    </>
  );
};

// =========================================================================

const HourlyDowHeatmap = ({ hourly }) => {
  // Grid: 7 days × N hours. Compute max for scaling.
  const max = Math.max(...hourly.map((h) => h.footfall)) || 1;
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hours = [...new Set(hourly.map((h) => h.hour))].sort((a, b) => a - b);

  const lookup = useMemo(() => {
    const m = {};
    hourly.forEach((h) => { m[`${h.day}|${h.hour}`] = h.footfall; });
    return m;
  }, [hourly]);

  const color = (v) => {
    const t = v / max;
    if (t < 0.2) return "#FAF9F6";
    return `rgba(20, 20, 43, ${(0.15 + t * 0.75).toFixed(3)})`;
  };
  const textColor = (v) => (v / max > 0.55 ? "#FFFFFF" : "#1C1C2E");

  return (
    <>
      <SectionHeader eyebrow="Footfall Rhythm" title="Hour × Day-of-week · aggregate footfall pattern" />
      <section className="ang-panel">
        <div className="overflow-x-auto p-4">
          <table className="text-xs mx-auto" data-testid="hourly-dow-heatmap">
            <thead>
              <tr>
                <th className="w-14 text-left px-2 py-1 text-[10px] uppercase tracking-widest text-ang-muted">Day</th>
                {hours.map((h) => (
                  <th key={h} className="min-w-[42px] text-center px-1 py-1 text-[10px] tabular-nums text-ang-muted font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((d, di) => (
                <tr key={d}>
                  <td className="px-2 py-1 font-serif text-sm">{d}</td>
                  {hours.map((h) => {
                    const v = lookup[`${d}|${h}`] || 0;
                    return (
                      <td key={h}
                          className="text-center tabular-nums px-1 py-2"
                          style={{ background: color(v), color: textColor(v) }}
                          title={`${d} ${h}:00 — ${v} visits`}>
                        {v > 0 ? Math.round(v / 10) * 10 : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-ang text-[10px] uppercase tracking-widest text-ang-muted flex items-center gap-2">
          Lower <span className="inline-block w-4 h-3" style={{ background: "#FAF9F6" }} />
          <span className="inline-block w-4 h-3" style={{ background: "rgba(20,20,43,0.3)" }} />
          <span className="inline-block w-4 h-3" style={{ background: "rgba(20,20,43,0.6)" }} />
          <span className="inline-block w-4 h-3" style={{ background: "rgba(20,20,43,0.9)" }} />
          Higher · values in visits ÷ 10
        </div>
      </section>
    </>
  );
};

// =========================================================================

const PLANOGRAM_ZONES = {
  veg: "Fresh Produce aisle",
  dairy: "Chilled counter",
  bakery: "Bakery display",
  groceries: "Staples aisle",
  toiletries: "Personal Care",
  household: "Household aisle",
  stationery: "Stationery corner",
  toys: "Kids corner",
  gift: "Front-of-store · seasonal",
};

const ACTION_VERBS = {
  cross: "Relocate",
  intra: "Face-out together",
};

const PlanogramSuggestions = ({ affinity }) => {
  const suggestions = useMemo(() => {
    if (!affinity?.top_pairs) return [];
    // Cross-category strong pairs → aisle-adjacency recommendation.
    // Intra-category strong pairs → shelf-facing recommendation.
    const scored = affinity.top_pairs
      .filter((p) => p.lift >= 1.5)
      .map((p) => {
        const cross = p.category_a !== p.category_b;
        const a = p.product_a.split(" · ")[0];
        const b = p.product_b.split(" · ")[0];
        // Pick a private-label side as the "move to" if any
        const anchor = p.is_private_a ? "b_to_a" : p.is_private_b ? "a_to_b" : "a_to_b";
        return { ...p, cross, a, b, anchor };
      });
    // De-duplicate by product pair (a|b or b|a)
    const seen = new Set();
    return scored.filter((s) => {
      const k = [s.a, s.b].sort().join("|");
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).slice(0, 8);
  }, [affinity]);

  if (!suggestions.length) return null;

  const exportPlanogramCSV = () => {
    const rows = suggestions.map((s) => ({
      action: s.cross ? "Relocate — cross aisle" : "Face-out — same aisle",
      product_a: s.product_a,
      category_a: s.category_a,
      product_b: s.product_b,
      category_b: s.category_b,
      lift: s.lift,
      confidence_pct: s.confidence_pct,
      target_zone: s.cross
        ? `${PLANOGRAM_ZONES[s.category_b.toLowerCase()] || s.category_b} ⇢ near ${PLANOGRAM_ZONES[s.category_a.toLowerCase()] || s.category_a}`
        : PLANOGRAM_ZONES[s.category_a.toLowerCase()] || s.category_a,
    }));
    downloadCSV(
      `angadya-planogram-${stamp()}.csv`,
      [
        { key: "action", label: "Action" },
        { key: "product_a", label: "Product A" },
        { key: "category_a", label: "Category A" },
        { key: "product_b", label: "Product B" },
        { key: "category_b", label: "Category B" },
        { key: "lift", label: "Lift" },
        { key: "confidence_pct", label: "Confidence %" },
        { key: "target_zone", label: "Recommended Zone" },
      ],
      rows,
    );
  };

  return (
    <>
      <SectionHeader
        eyebrow="Planogram Suggestions"
        title="Auto-generated · aisle adjacency &amp; face-out plays"
        rightSlot={
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-planogram"
            onClick={exportPlanogramCSV}
          />
        }
      />
      <section className="ang-panel" data-testid="planogram-suggestions">
        <ul className="divide-y divide-ang">
          {suggestions.map((s, i) => {
            const zoneA = PLANOGRAM_ZONES[s.category_a.toLowerCase()] || s.category_a;
            const zoneB = PLANOGRAM_ZONES[s.category_b.toLowerCase()] || s.category_b;
            const primary = s.anchor === "a_to_b" ? { moved: s.a, anchor: s.b, from: zoneA, to: zoneB } : { moved: s.b, anchor: s.a, from: zoneB, to: zoneA };
            const impactBand =
              s.lift >= 2.2 ? { label: "High", color: "#2F8B5E" } :
              s.lift >= 1.8 ? { label: "Med", color: "#B8842A" } :
              { label: "Try", color: "#6B6B7B" };
            return (
              <li key={i} className="px-4 sm:px-6 py-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5"
                      style={{
                        background: s.cross ? "rgba(201,161,92,0.18)" : "rgba(47,139,94,0.14)",
                        color: s.cross ? "#8A6B2E" : "#2F6E4A",
                      }}
                    >
                      {s.cross ? "Cross-aisle move" : "Same-aisle face-out"}
                    </span>
                    <span
                      className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5"
                      style={{ background: "#FAF9F6", color: impactBand.color, border: `1px solid ${impactBand.color}33` }}
                    >
                      {impactBand.label} · lift {s.lift.toFixed(2)}
                    </span>
                  </div>
                  <div className="font-serif text-base sm:text-lg leading-snug">
                    {s.cross ? (
                      <>
                        <span className="text-ang-muted text-xs uppercase tracking-widest mr-1">{ACTION_VERBS.cross}</span>
                        <span className="font-semibold">{primary.moved}</span>
                        <span className="text-ang-muted"> from </span>
                        <span>{primary.from}</span>
                        <span className="text-ang-muted"> next to </span>
                        <span className="font-semibold">{primary.anchor}</span>
                        <span className="text-ang-muted"> in </span>
                        <span>{primary.to}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-ang-muted text-xs uppercase tracking-widest mr-1">{ACTION_VERBS.intra}</span>
                        <span className="font-semibold">{s.a}</span>
                        <span className="text-ang-muted"> and </span>
                        <span className="font-semibold">{s.b}</span>
                        <span className="text-ang-muted"> at eye level in </span>
                        <span>{zoneA}</span>
                      </>
                    )}
                  </div>
                  <div className="text-[11px] text-ang-muted mt-1">
                    Confidence {s.confidence_pct}% · categories {s.category_a}
                    {s.cross ? ` + ${s.category_b}` : ""}
                    {(s.is_private_a || s.is_private_b) && (
                      <span className="ml-2 inline-flex items-center gap-1" style={{ color: "#8A6B2E" }}>
                        <span className="w-1 h-2.5 inline-block" style={{ background: "#C9A15C" }} /> Own-label pair
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right tabular-nums hidden sm:block">
                  <div className="text-[10px] uppercase tracking-widest text-ang-muted">Est. basket lift</div>
                  <div className="font-serif text-xl" style={{ color: impactBand.color }}>
                    +{Math.round((s.lift - 1) * 100)}%
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="px-6 py-3 border-t border-ang text-[10px] uppercase tracking-widest text-ang-muted">
          Derived from basket-affinity matrix · re-runs weekly on live PoS data
        </div>
      </section>
    </>
  );
};

// =========================================================================

const CategoryGrowthTable = ({ cats }) => (
  <>
    <SectionHeader eyebrow="Category · Year-over-Year" title="Which categories are pulling the chain up" />
    <section className="ang-panel">
      <table className="w-full text-sm" data-testid="cat-growth-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
            <th className="text-left px-4 py-3">Category</th>
            <th className="text-right px-4 py-3 w-40">Revenue · 30D</th>
            <th className="text-right px-4 py-3 w-32">YoY %</th>
            <th className="text-left px-4 py-3 w-56">Bar</th>
          </tr>
        </thead>
        <tbody>
          {cats.map((c, i) => {
            const abs = Math.abs(c.yoy_pct);
            const pct = Math.min(100, abs * 3);
            return (
              <tr key={c.category_id} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                <td className="px-4 py-2.5 font-medium">{c.category}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(c.revenue_30d, { compact: true })}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold"
                    style={{ color: c.yoy_pct >= 0 ? "#2F8B5E" : "#B7402A" }}>
                  {fmtPct(c.yoy_pct)}
                </td>
                <td className="px-4 py-2.5">
                  <div className="h-1.5 bg-[#E7E4DC] relative">
                    <div className="absolute inset-y-0 left-0"
                         style={{
                           background: c.yoy_pct >= 0 ? "#2F8B5E" : "#B7402A",
                           width: `${pct}%`,
                         }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  </>
);

// =========================================================================

const SectionHeader = ({ eyebrow, title, rightSlot }) => (
  <div className="flex items-end justify-between border-b border-ang pb-3 gap-3 flex-wrap">
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ang-muted">{eyebrow}</div>
      <h2 className="font-serif text-2xl mt-1">{title}</h2>
    </div>
    {rightSlot}
  </div>
);

export default Analytics;
